import CarouselComponent from "../lib/carousel_component";
import { ImageLoader } from "../lib/image_loader";
import { ImageTransform } from "../lib/image_transform";
import { ZoomPanController } from "../lib/zoom_pan_controller";
import { SlideRenderer } from "../lib/slide_renderer";
import { AutoPan } from "../lib/auto_pan";

export const CarouselSlideConfig = {
    componentTypeKey: 'CarouselSlide',
    baseHtmlClass: 'carousel__slide',
    selector: '.carousel__slide',
    imageSelector: '.carousel__image',
    activeClassModifier: 'active',
    loadingClassModifier: 'loading',
    loadedClassModifier: 'loaded',
    errorClassModifier: 'error',
    timeoutClassModifier: 'timeout',
    placeholderLoadedClassModifier: 'placeholder-loaded',
    fullLoadedClassModifier: 'full-loaded',
    coverClassModifier: 'cover',
    containClassModifier: 'contain',
    zoomableClassModifier: 'zoomable',
    zoomedClassModifier: 'zoomed',
    initializedClassModifier: 'initialized',
    imageSlideTypeModifier: 'image',
    contentSlideTypeModifier: 'content',
    defaultImageFit: 'contain',
    minZoom: 1,
    maxZoom: 3,
    doubleClickZoom: 2, // zoom level for double-click zoom
    autoPanSpeed: 0.5, // pixels per frame
    autoPanPause: 3000, // milliseconds to pause at edges
    autoPanEdgeEase: 50, // pixels from edge where easing begins
    autoPanMinSpeed: 0.1, // minimum speed when near edges
    autoPanStartupDelay: 2000, // milliseconds to wait before starting auto-pan
};

export class CarouselSlide extends CarouselComponent {
    
    static get config() {
        return CarouselSlideConfig;
    }

    constructor(id_or_element, carousel, slideConfig = null) {
        super(id_or_element, carousel, CarouselSlideConfig);
        
        this.carousel = carousel;
        this.isActive = this.element.classList.contains(CarouselSlideConfig.activeClassModifier);
        this.slideConfig = slideConfig;
        
        // Determine slide type and set image fit conditionally
        this.hasImageComponents = this.shouldInitializeImageComponents();
        this.slideType = this.hasImageComponents ? 'image' : 'content';
        this.imageFit = this.slideType === 'image' 
            ? ((slideConfig && slideConfig.image_fit) ? slideConfig.image_fit : CarouselSlideConfig.defaultImageFit)
            : null;
        this.isZoomable = this.determineZoomability(slideConfig);
        
        // Process auto-pan configuration into instance variables
        this.processAutoPanConfig(slideConfig);
        
        // Process zoom configuration into instance variables
        this.processZoomConfig(slideConfig);
        
        // Initialize components conditionally
        
        if (this.hasImageComponents) {
            this.imageLoader = new ImageLoader(this.element, CarouselSlideConfig);
            this.imageTransform = new ImageTransform(this.element, this.imageFit);
            this.zoomPanController = new ZoomPanController(this.element, {
                minZoom: this.minZoom,
                maxZoom: this.maxZoom,
                doubleClickZoom: this.doubleClickZoom
            });
            this.autoPan = new AutoPan(this.imageTransform, {
                speed: this.autoPanSpeed,
                pauseAtEdges: this.autoPanPause,
                edgeEaseDistance: this.autoPanEdgeEase,
                minSpeed: this.autoPanMinSpeed,
                startupDelay: this.autoPanStartupDelay,
                enabled: this.autoPanEnabled
            });
        } else {
            this.imageLoader = null;
            this.imageTransform = null;
            this.zoomPanController = null;
            this.autoPan = null;
        }
        
        this.slideRenderer = new SlideRenderer(this.element, CarouselSlideConfig, this.carousel.config);

        // The plain helpers above (not Components) hold nothing the base can reach, so tear them
        // down here. Registered via onDestroy so the base destroy() runs it exactly once (LIFO,
        // guarded by isDestroyed) — safe against the parent carousel and the reaper both destroying
        // this slide.
        this.onDestroy(() => {
            this.imageLoader?.destroy();
            this.imageTransform?.destroy();
            this.zoomPanController?.destroy();
            this.autoPan?.destroy();
            this.slideRenderer?.destroy();
        });

        this.setupScrollSnap();
        this.setupComponentCommunication();
        this.loadImage();
        
        // Enable interactions if this slide is initially active and zoomable
        if (this.slideType === 'image') {
            this.zoomPanController.setEnabled(this.isActive && this.isZoomable);
        }
        
        // Don't render immediately - let carousel control initial render after setting proximity states
    }

    setupScrollSnap() {
        Object.assign(this.element.style, {
            scrollSnapAlign: 'start',
            flexShrink: '0'
        });
    }
    
    setupComponentCommunication() {
        if (this.slideType !== 'image') return;
        
        // Connect ZoomPanController to ImageTransform
        this.zoomPanController.setImageTransform(this.imageTransform);
        
        // ImageLoader events
        this.imageLoader.addEventListener('dimensionsKnown', (data) => {
            this.imageTransform.setImageDimensions(data.naturalWidth, data.naturalHeight);
        });
        
        this.imageLoader.addEventListener('loadingStateChanged', (data) => {
            this.slideRenderer.setLoadingState(data.isLoading);
        });
        
        this.imageLoader.addEventListener('loadedStateChanged', (data) => {
            this.slideRenderer.setLoadedState(data.isLoaded);
        });
        
        this.imageLoader.addEventListener('errorStateChanged', (data) => {
            this.slideRenderer.setErrorState(data.hasError);
        });
        
        this.imageLoader.addEventListener('timeoutStateChanged', (data) => {
            this.slideRenderer.setTimeoutState(data.hasTimeout);
        });
        
        this.imageLoader.addEventListener('placeholderLoadedStateChanged', (data) => {
            this.slideRenderer.setPlaceholderLoadedState(data.hasPlaceholder);
        });
        
        this.imageLoader.addEventListener('fullImageLoadedStateChanged', (data) => {
            this.slideRenderer.setFullImageLoadedState(data.hasFullImage);
        });
        
        // ImageTransform events
        this.imageTransform.addEventListener('baseTransformCalculated', () => {
            this.slideRenderer.setInitialized(true);
            this.updatePanCapability();
            
            // Start auto-pan if this slide is active and not zoomed
            if (this.isActive) {
                const transformState = this.imageTransform.getState();
                if (!transformState.isZoomed) {
                    this.autoPan.start();
                }
            }
        });
        
        this.imageTransform.addEventListener('transformApplied', () => {
            this.updatePanCapability();
        });
        
        this.imageTransform.addEventListener('zoomChanged', (data) => {
            this.slideRenderer.setZoomed(data.isZoomed);
            this.updatePanCapability();
            
            // Stop auto-pan when zoomed, resume when back to normal
            if (data.isZoomed) {
                this.autoPan.stop();
            } else if (this.isActive) {
                // Only restart if this slide is active
                this.autoPan.start();
            }
        });
        
        // ZoomPanController events
        this.zoomPanController.addEventListener('zoomRequested', (data) => {
            this.imageTransform.setZoom(data.zoom, data.centerX, data.centerY);
        });
        
        this.zoomPanController.addEventListener('panRequested', (data) => {
            this.imageTransform.pan(data.deltaX, data.deltaY);
        });
        
        this.zoomPanController.addEventListener('resetZoomRequested', () => {
            this.imageTransform.resetZoom();
        });
        
        this.zoomPanController.addEventListener('panStart', () => {
            this.slideRenderer.setCursorGrabbing();
            // Pause auto-pan during manual panning
            this.autoPan.pause();
        });
        
        this.zoomPanController.addEventListener('panEnd', () => {
            this.slideRenderer.resetCursor();
            // Re-enable transitions after panning
            this.imageTransform.applyTransform(false);
            // Resume auto-pan if not zoomed and slide is active
            const transformState = this.imageTransform.getState();
            if (!transformState.isZoomed && this.isActive) {
                this.autoPan.resume();
            }
        });
    }
    
    updatePanCapability() {
        if (this.slideType !== 'image') return;
        const canPan = this.imageTransform.canPan();
        this.slideRenderer.setCanPan(canPan);
    }

    loadImage() {
        if (this.slideType !== 'image') return;
        
        if (this.slideConfig) {
            // Config-based slide
            this.imageLoader.loadFromConfig(this.slideConfig);
        } else {
            // Existing content slide
            this.imageLoader.loadExistingContent();
        }
    }

    setActive(active) {
        this.isActive = active;
        
        if (this.slideType === 'image') {
            // Reset zoom when slide becomes inactive
            if (!active) {
                const transformState = this.imageTransform.getState();
                if (transformState.isZoomed) {
                    this.imageTransform.resetZoom();
                }
                // Stop auto-pan when slide becomes inactive
                this.autoPan.stop();
            }
            
            // Enable/disable interactions based on active state
            this.zoomPanController.setEnabled(active && this.isZoomable);
            
            // Recalculate base transform when becoming active
            if (active) {
                const dimensions = this.imageLoader.getDimensions();
                if (dimensions.naturalWidth && dimensions.naturalHeight) {
                    this.imageTransform.setImageDimensions(dimensions.naturalWidth, dimensions.naturalHeight);
                }
                
                // Start auto-pan if slide is active and not zoomed
                const transformState = this.imageTransform.getState();
                if (!transformState.isZoomed) {
                    this.autoPan.start();
                }
            }
        }
        
        // Update renderer
        this.slideRenderer.setActive(active);
    }

    setActiveWithoutRender(active) {
        this.isActive = active;
        
        if (this.slideType === 'image') {
            // Reset zoom when slide becomes inactive
            if (!active) {
                const transformState = this.imageTransform.getState();
                if (transformState.isZoomed) {
                    this.imageTransform.resetZoom();
                }
                // Stop auto-pan when slide becomes inactive
                this.autoPan.stop();
            }
            
            // Enable/disable interactions based on active state
            this.zoomPanController.setEnabled(active && this.isZoomable);
            
            // Recalculate base transform when becoming active
            if (active) {
                const dimensions = this.imageLoader.getDimensions();
                if (dimensions.naturalWidth && dimensions.naturalHeight) {
                    this.imageTransform.setImageDimensions(dimensions.naturalWidth, dimensions.naturalHeight);
                }
                
                // Start auto-pan if slide is active and not zoomed
                const transformState = this.imageTransform.getState();
                if (!transformState.isZoomed) {
                    this.autoPan.start();
                }
            }
        }
        
        // Update renderer state without triggering render
        this.slideRenderer.setActiveWithoutRender(active);
    }

    setProximityState(signedDistance) {
        if (this.slideRenderer) {
            this.slideRenderer.setProximityState(signedDistance);
        }
    }

    determineZoomability(slideConfig) {
        // If explicitly set in config, use that
        if (slideConfig && typeof slideConfig.zoomable !== 'undefined') {
            return slideConfig.zoomable;
        }
        // Use carousel's default zoomability setting
        return this.carousel.isZoomableByDefault();
    }

    processAutoPanConfig(slideConfig) {
        // Set auto-pan instance variables with global defaults
        this.autoPanSpeed = CarouselSlideConfig.autoPanSpeed;
        this.autoPanPause = CarouselSlideConfig.autoPanPause;
        this.autoPanEdgeEase = CarouselSlideConfig.autoPanEdgeEase;
        this.autoPanMinSpeed = CarouselSlideConfig.autoPanMinSpeed;
        this.autoPanStartupDelay = CarouselSlideConfig.autoPanStartupDelay;
        this.autoPanEnabled = true;
        
        // Override with per-slide config if provided
        if (slideConfig) {
            if (typeof slideConfig.auto_pan_speed !== 'undefined') {
                this.autoPanSpeed = slideConfig.auto_pan_speed;
            }
            if (typeof slideConfig.auto_pan_pause !== 'undefined') {
                this.autoPanPause = slideConfig.auto_pan_pause;
            }
            if (typeof slideConfig.auto_pan_edge_ease !== 'undefined') {
                this.autoPanEdgeEase = slideConfig.auto_pan_edge_ease;
            }
            if (typeof slideConfig.auto_pan_min_speed !== 'undefined') {
                this.autoPanMinSpeed = slideConfig.auto_pan_min_speed;
            }
            if (typeof slideConfig.auto_pan_startup_delay !== 'undefined') {
                this.autoPanStartupDelay = slideConfig.auto_pan_startup_delay;
            }
            if (typeof slideConfig.auto_pan_enabled !== 'undefined') {
                this.autoPanEnabled = slideConfig.auto_pan_enabled;
            }
        }
    }
    
    processZoomConfig(slideConfig) {
        // Set zoom instance variables with global defaults
        this.minZoom = CarouselSlideConfig.minZoom;
        this.maxZoom = CarouselSlideConfig.maxZoom;
        this.doubleClickZoom = CarouselSlideConfig.doubleClickZoom;
        
        // Override with per-slide config if provided
        if (slideConfig) {
            if (typeof slideConfig.min_zoom !== 'undefined') {
                this.minZoom = slideConfig.min_zoom;
            }
            if (typeof slideConfig.max_zoom !== 'undefined') {
                this.maxZoom = slideConfig.max_zoom;
            }
            if (typeof slideConfig.double_click_zoom !== 'undefined') {
                this.doubleClickZoom = slideConfig.double_click_zoom;
            }
        }
    }

    shouldInitializeImageComponents() {
        if (this.slideConfig) {
            // Config-based slide - check if it has image data
            return !!(this.slideConfig.url || this.slideConfig.src || this.slideConfig.type === 'image');
        } else {
            // Existing content - only if it's a simple image that should be zoomable
            const imgs = this.element.querySelectorAll('img');
            const hasComplexContent = !!(
                this.element.querySelector('.related_item') ||
                this.element.querySelector('.carousel__image') ||
                this.element.querySelector('a') ||
                this.element.querySelector('.js--analytics_watcher')
            );
            
            // Only handle if there's exactly one img and no complex content
            return imgs.length === 1 && !hasComplexContent;
        }
    }
    
    render() {
        // Update renderer with current states
        if (this.slideType === 'image') {
            const loaderStates = this.imageLoader.getStates();
            const transformState = this.imageTransform.getState();
            
            this.slideRenderer.updateStates({
                slideType: this.slideType,
                isActive: this.isActive,
                isLoading: loaderStates.isLoading,
                isLoaded: loaderStates.isLoaded,
                hasError: loaderStates.hasError,
                hasTimeout: loaderStates.hasTimeout,
                hasPlaceholder: loaderStates.hasPlaceholder,
                hasFullImage: loaderStates.hasFullImage,
                isZoomable: this.isZoomable,
                isZoomed: transformState.isZoomed,
                imageFit: this.imageFit,
                canPan: transformState.canPan
            });
        } else {
            // For content slides without image components
            this.slideRenderer.updateStates({
                slideType: this.slideType,
                isActive: this.isActive,
                isLoaded: true,
                // No image-related states for content slides
            });
        }
    }
    
    // No destroy() override — the plain-helper teardown is registered via onDestroy() in the
    // constructor; the base Component#destroy() runs it and deregisters this slide.
}